/**
 * ============================================================================
 * Iron Meridian - Globe Fail-Fast Policy
 * ============================================================================
 * 
 * ENVIRONMENT-AWARE ERROR HANDLING
 * 
 * This module implements the fail-fast policy for globe rendering:
 * 
 * Development & Test Environments:
 * - Missing countries = HARD ERROR (throws)
 * - Duplicate paths = HARD ERROR (throws)
 * - Render contract violations = HARD ERROR (throws)
 * 
 * Production Environment:
 * - Graceful fallback to neutral rendering
 * - Structured warnings logged for telemetry
 * - UI remains functional even with data issues
 * 
 * This ensures bugs are caught early in development while maintaining
 * a stable user experience in production.
 */

import { getGlobalGlobeContract } from './globeRenderContract';
import { validateCoverage, CoverageStatus } from './assertCountryCoverage';

// ============================================================================
// ENVIRONMENT DETECTION
// ============================================================================

/**
 * Detect current environment
 */
export function getEnvironment() {
  // Node.js environment
  if (typeof process !== 'undefined' && process.env?.NODE_ENV) {
    return process.env.NODE_ENV;
  }
  
  // Vite environment
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    if (import.meta.env.MODE === 'test') return 'test';
    if (import.meta.env.DEV) return 'development';
    if (import.meta.env.PROD) return 'production';
  }
  
  // Browser fallback - check hostname
  if (typeof window !== 'undefined') {
    const hostname = window.location?.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'development';
    }
  }
  
  return 'production';
}

export function isDevelopment() {
  return getEnvironment() === 'development';
}

export function isTest() {
  return getEnvironment() === 'test';
}

export function isProduction() {
  return getEnvironment() === 'production';
}

export function shouldFailFast() {
  return isDevelopment() || isTest();
}

// ============================================================================
// ERROR CLASSES
// ============================================================================

/**
 * Base class for globe integrity errors
 */
export class GlobeIntegrityError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = 'GlobeIntegrityError';
    this.details = details;
    this.timestamp = new Date().toISOString();
    this.environment = getEnvironment();
  }

  toTelemetry() {
    return {
      type: this.name,
      message: this.message,
      details: this.details,
      timestamp: this.timestamp,
      environment: this.environment
    };
  }
}

/**
 * Error for missing country paths
 */
export class MissingCountryError extends GlobeIntegrityError {
  constructor(missingCountries) {
    super(`Missing ${missingCountries.length} country path(s) in SVG`, {
      missingCountries,
      count: missingCountries.length
    });
    this.name = 'MissingCountryError';
  }
}

/**
 * Error for duplicate country paths
 */
export class DuplicateCountryError extends GlobeIntegrityError {
  constructor(duplicates) {
    super(`Duplicate country path(s) detected: ${duplicates.join(', ')}`, {
      duplicates,
      count: duplicates.length
    });
    this.name = 'DuplicateCountryError';
  }
}

/**
 * Error for render contract violations
 */
export class RenderContractError extends GlobeIntegrityError {
  constructor(violation) {
    super(`Render contract violated: ${violation.error}`, violation);
    this.name = 'RenderContractError';
  }
}

// ============================================================================
// FAIL-FAST HANDLERS
// ============================================================================

/**
 * Handle a globe integrity error based on environment
 * @param {GlobeIntegrityError} error - The error to handle
 * @param {Function} fallbackFn - Optional fallback to execute in production
 */
export function handleIntegrityError(error, fallbackFn = null) {
  // Log structured error for telemetry in all environments
  const telemetry = error.toTelemetry();
  console.error('[Globe Integrity]', JSON.stringify(telemetry));

  if (shouldFailFast()) {
    // Development/Test: Throw immediately to fail fast
    throw error;
  } else {
    // Production: Log warning and execute fallback if provided
    console.warn('[Globe Integrity] Graceful fallback activated:', error.message);
    
    if (typeof fallbackFn === 'function') {
      try {
        return fallbackFn();
      } catch (fallbackError) {
        console.error('[Globe Integrity] Fallback also failed:', fallbackError);
      }
    }
  }
}

/**
 * Validate country coverage with fail-fast behavior
 * @param {string[]} svgCountries - Country IDs from SVG paths
 * @param {object} gameState - Current game state
 * @returns {object} Coverage report
 */
export function validateCoverageWithPolicy(svgCountries, gameState = {}) {
  try {
    const report = validateCoverage(svgCountries, gameState, {
      failOnError: shouldFailFast(),
      failOnWarning: false,
      silent: isProduction()
    });
    return report;
  } catch (error) {
    if (error.report) {
      // Coverage assertion error with report
      const integrityError = new GlobeIntegrityError(
        `Coverage validation failed: ${error.report.summary.errorCount} error(s)`,
        error.report
      );
      handleIntegrityError(integrityError);
    }
    throw error;
  }
}

/**
 * Verify render contract with fail-fast behavior
 * @returns {object} Verification result
 */
export function verifyRenderContractWithPolicy() {
  const contract = getGlobalGlobeContract();
  const result = contract.verify();

  if (!result.passed) {
    const error = new RenderContractError(result);
    handleIntegrityError(error, () => {
      // Fallback: Log but continue
      return { passed: false, fallback: true };
    });
  }

  return result;
}

/**
 * Check for duplicate country paths in SVG
 * @param {Element} countriesGroup - SVG group element
 * @returns {string[]} Array of duplicate IDs (empty if none)
 */
export function checkForDuplicates(countriesGroup) {
  if (!countriesGroup) return [];

  const paths = countriesGroup.querySelectorAll('path[data-country-id]');
  const seen = new Set();
  const duplicates = [];

  paths.forEach(path => {
    const id = path.getAttribute('data-country-id');
    if (seen.has(id)) {
      duplicates.push(id);
    }
    seen.add(id);
  });

  if (duplicates.length > 0) {
    const error = new DuplicateCountryError(duplicates);
    handleIntegrityError(error, () => {
      // Fallback: Remove duplicates (keep first)
      const toRemove = [];
      const seenOnce = new Set();
      paths.forEach(path => {
        const id = path.getAttribute('data-country-id');
        if (seenOnce.has(id)) {
          toRemove.push(path);
        }
        seenOnce.add(id);
      });
      toRemove.forEach(path => path.remove());
      return duplicates;
    });
  }

  return duplicates;
}

// ============================================================================
// INTEGRATION HELPERS
// ============================================================================

/**
 * Full globe validation - runs all checks
 * @param {Element} countriesGroup - SVG group element
 * @param {object} geoData - Loaded geo data
 * @param {object} gameState - Current game state
 * @returns {object} Validation results
 */
export function validateGlobeIntegrity(countriesGroup, geoData, gameState = {}) {
  const results = {
    timestamp: new Date().toISOString(),
    environment: getEnvironment(),
    checks: {
      duplicates: { passed: false, data: null },
      coverage: { passed: false, data: null },
      contract: { passed: false, data: null }
    },
    overallPassed: false
  };

  try {
    // Check 1: Duplicates
    const duplicates = checkForDuplicates(countriesGroup);
    results.checks.duplicates = {
      passed: duplicates.length === 0,
      data: { duplicates }
    };

    // Check 2: Coverage
    if (geoData?.countries?.features) {
      const svgCountries = geoData.countries.features.map(f => f.id).filter(Boolean);
      const report = validateCoverageWithPolicy(svgCountries, gameState);
      results.checks.coverage = {
        passed: report.summary.errorCount === 0,
        data: report.summary
      };
    }

    // Check 3: Render Contract
    const contract = getGlobalGlobeContract();
    if (contract.getStatus().initialized) {
      const verification = verifyRenderContractWithPolicy();
      results.checks.contract = {
        passed: verification.passed,
        data: verification
      };
    } else {
      results.checks.contract = {
        passed: true,
        data: { note: 'Contract not yet initialized' }
      };
    }

    // Overall result
    results.overallPassed = Object.values(results.checks).every(c => c.passed);

  } catch (error) {
    // If we get here in production, something went wrong with fallback
    results.error = error.message;
    results.overallPassed = false;
  }

  // Log summary
  if (!results.overallPassed && shouldFailFast()) {
    console.error('[Globe Integrity] Validation FAILED:', results);
  } else if (!results.overallPassed) {
    console.warn('[Globe Integrity] Validation issues (graceful mode):', results);
  } else if (isDevelopment()) {
    console.log('[Globe Integrity] ✅ All checks passed');
  }

  return results;
}

/**
 * React hook for globe integrity validation
 * Use this in WorldMap component to integrate fail-fast policy
 */
export function useGlobeIntegrity(countriesGroupRef, geoData, gameState, enabled = true) {
  if (!enabled || isProduction()) {
    return { validated: true, results: null };
  }

  // This is called during render - validation happens synchronously
  if (countriesGroupRef?.current && geoData) {
    const results = validateGlobeIntegrity(
      countriesGroupRef.current,
      geoData,
      gameState
    );
    return { validated: results.overallPassed, results };
  }

  return { validated: false, results: null };
}

export default {
  getEnvironment,
  isDevelopment,
  isTest,
  isProduction,
  shouldFailFast,
  handleIntegrityError,
  validateCoverageWithPolicy,
  verifyRenderContractWithPolicy,
  checkForDuplicates,
  validateGlobeIntegrity,
  useGlobeIntegrity,
  // Error classes
  GlobeIntegrityError,
  MissingCountryError,
  DuplicateCountryError,
  RenderContractError
};
